import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { InventoryCategoriesResponse, InventoryCategory, PipeInventory } from '../model/pipe-inventory.model';


@Injectable({
  providedIn: 'root'
})
export class PipeInventoryService {
  private baseUrl = `${environment.apiServiceBaseUrl}/hawdco`;

  constructor(private http: HttpClient) {}

  // Get all pipe inventory items
  getPipeInventory(): Observable<PipeInventory[]> {
    return this.http.get<PipeInventory[]>(`${this.baseUrl}/pipe-inventory`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get pipe inventory by ID
  getPipeInventoryById(id: number): Observable<PipeInventory> {
    return this.http.get<PipeInventory>(`${this.baseUrl}/pipe-inventory/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Create new pipe inventory item
  createPipeInventory(pipeInventory: Partial<PipeInventory>): Observable<PipeInventory> {
    return this.http.post<PipeInventory>(`${this.baseUrl}/pipe-inventory`, pipeInventory)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Update pipe inventory item
  updatePipeInventory(id: number, pipeInventory: Partial<PipeInventory>): Observable<PipeInventory> {
    return this.http.put<PipeInventory>(`${this.baseUrl}/pipe-inventory/${id}`, pipeInventory)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Delete pipe inventory item
  deletePipeInventory(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/pipe-inventory/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get inventory categories
  getInventoryCategories(): Observable<InventoryCategoriesResponse> {
    return this.http.get<InventoryCategoriesResponse>(`${this.baseUrl}/inventory-categories`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get inventory category by ID
  getInventoryCategoryById(id: number): Observable<InventoryCategory> {
    return this.http.get<InventoryCategory>(`${this.baseUrl}/inventory-categories/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Create new inventory category
  createInventoryCategory(category: Partial<InventoryCategory>): Observable<InventoryCategory> {
    return this.http.post<InventoryCategory>(`${this.baseUrl}/inventory-categories`, category)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Update inventory category
  updateInventoryCategory(id: number, category: Partial<InventoryCategory>): Observable<InventoryCategory> {
    return this.http.put<InventoryCategory>(`${this.baseUrl}/inventory-categories/${id}`, category)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Delete inventory category
  deleteInventoryCategory(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/inventory-categories/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Search pipe inventory with filters
  searchPipeInventory(
    search?: string,
    areaCode?: number,
    expCategoryId?: number,
    zone?: number,
    status?: string
  ): Observable<PipeInventory[]> {
    let params = new HttpParams();
    
    if (search) params = params.set('search', search);
    if (areaCode) params = params.set('areaCode', areaCode.toString());
    if (expCategoryId) params = params.set('expCategoryId', expCategoryId.toString());
    if (zone) params = params.set('zone', zone.toString());
    if (status) params = params.set('status', status);

    return this.http.get<PipeInventory[]>(`${this.baseUrl}/pipe-inventory/search`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Update stock quantity
  updateStockQuantity(pipeId: number, quantity: number): Observable<PipeInventory> {
    return this.http.patch<PipeInventory>(`${this.baseUrl}/pipe-inventory/${pipeId}/stock`, { qtyOnStock: quantity })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get low stock items
  getLowStockItems(): Observable<PipeInventory[]> {
    return this.http.get<PipeInventory[]>(`${this.baseUrl}/pipe-inventory/low-stock`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get inventory summary statistics - calculated locally in component
  // This method is removed as the summary is calculated from local data

  // Error handling
  private handleError(error: any): Observable<never> {
    let errorMessage = 'An error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      
      if (error.error && error.error.message) {
        errorMessage = error.error.message;
      }
    }

    console.error('Pipe Inventory API Error:', error);
    return throwError(() => new Error(errorMessage));
  }
}
